import { CustomCard } from '@/components/custom-card';
import { SubheadingSm } from '@/components/custom-titles';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/numberFormat';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { MONTHLY_CHART_COLOR } from '../constants';
import { InsuranceChartItem } from '../model';

interface InsuranceMonthlyChartProps {
  data?: InsuranceChartItem[];
  isLoading?: boolean;
  className?: string;
}

const MONTH_NAMES = [
  'Yan',
  'Fev',
  'Mar',
  'Apr',
  'May',
  'Iyun',
  'Iyul',
  'Avg',
  'Sen',
  'Okt',
  'Noy',
  'Dek',
];

const GRADIENT_ID = 'insurance-monthly-gradient';

const chartConfig = {
  contract_count: { label: ' ', color: MONTHLY_CHART_COLOR },
};

/** "YYYY-MM" dan oy nomini oladi; kutilmagan format kelsa o'zini qaytaradi. */
const getMonthLabel = (month: string) => {
  const index = Number(month?.split('-')[1]) - 1;
  return MONTH_NAMES[index] ?? month;
};

/** Oylar kesimida shartnomalar dinamikasi. */
export const InsuranceMonthlyChart = ({
  data,
  isLoading,
  className,
}: InsuranceMonthlyChartProps) => {
  const { t } = useTranslation();

  const chartData = (data || []).map((item) => ({
    month: getMonthLabel(item.month),
    contract_count: item.contract_count || 0,
  }));

  return (
    <CustomCard className={cn('flex w-full flex-col gap-4', className)}>
      <SubheadingSm title={t('insurance_monthly_dynamics')} />

      {isLoading ? (
        <div className="flex h-[260px] items-center justify-center">
          <div className="progress" />
        </div>
      ) : chartData.length ? (
        <ChartContainer config={chartConfig} className="h-[260px] w-full">
          <AreaChart
            data={chartData}
            margin={{ left: 0, right: 10, top: 10, bottom: 0 }}
          >
            <defs>
              <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={MONTHLY_CHART_COLOR}
                  stopOpacity={0.45}
                />
                <stop
                  offset="100%"
                  stopColor={MONTHLY_CHART_COLOR}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />

            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={44}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickFormatter={(value: number) => formatNumber(value)}
            />

            <ChartTooltip
              cursor={{
                stroke: MONTHLY_CHART_COLOR,
                strokeWidth: 1,
                strokeDasharray: '4 4',
              }}
              content={({ label, payload }) => {
                if (!payload?.length) return null;
                return (
                  <div className="rounded-md bg-foreground px-3 py-2 text-sm shadow">
                    <span className="font-medium text-[#101014]">{label}</span>
                    <span className="ml-2 text-[#101014]">
                      : {formatNumber(Number(payload[0]?.value))} {t('ta')}
                    </span>
                  </div>
                );
              }}
            />

            <Area
              type="monotone"
              dataKey="contract_count"
              stroke={MONTHLY_CHART_COLOR}
              strokeWidth={2}
              fill={`url(#${GRADIENT_ID})`}
            />
          </AreaChart>
        </ChartContainer>
      ) : (
        <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
          {t('no_data')}
        </div>
      )}
    </CustomCard>
  );
};
